import React, { useState } from 'react';
import { View } from 'react-native';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { ActivityIndicator } from 'react-native-paper';
import {
  ADMIN_CREATE_DISCOUNT_CODE,
  ADMIN_SET_DISCOUNT_CODE_ACTIVE,
  GET_ADMIN_DISCOUNT_CODES,
} from '@/shared/queries/admin';
import {
  MutationAdminCreateDiscountCodeArgs,
  MutationAdminSetDiscountCodeActiveArgs,
  MutationResult,
  QueryResult,
} from '@/shared/types/generated';
import StaticInput from '@/shared/components/StaticInput';
import DateInput from '@/shared/components/DateInput';
import Button from '@/shared/components/Button';
import { LoraText } from '@/shared/components/StyledText';
import SentryService from '@/services/sentry/Sentry.service';
import useStore from '@/stores/useStore';
import { green, red } from '@/shared/styles/colors';
import {
  ActionLink,
  ConfirmAction,
  EmptyState,
  ErrorState,
  Muted,
  Panel,
  Pill,
  RowCard,
  SectionTitle,
  Small,
  Stack,
  Strong,
} from './AdminUI';
import { plural, shortDate, toDate } from './format';

// Same rules as the server (mutations.adminSchema.ts)
const CODE_TEXT_REGEX = /^[A-Z0-9_-]{3,32}$/;
// Stripe needs a minimum charge, so a 100% code would make bookings fail
const MAX_DISCOUNT_PERCENTAGE = 90;

const serverMessage = (error: unknown) =>
  error instanceof ApolloError ? error.graphQLErrors[0]?.message : undefined;

const PromoCodesPanel = ({ isDesktop }: { isDesktop: boolean }) => {
  const { setSnackbarState } = useStore();
  const [codeText, setCodeText] = useState('');
  const [percent, setPercent] = useState('');
  const [expiry, setExpiry] = useState<Date | undefined>(undefined);
  const [formError, setFormError] = useState<string | null>(null);

  const { data, loading, error, refetch } = useQuery<
    QueryResult<'adminDiscountCodes'>
  >(GET_ADMIN_DISCOUNT_CODES, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });
  const [createCode, { loading: creating }] = useMutation<
    MutationResult<'adminCreateDiscountCode'>,
    MutationAdminCreateDiscountCodeArgs
  >(ADMIN_CREATE_DISCOUNT_CODE);
  const [setActive] = useMutation<
    MutationResult<'adminSetDiscountCodeActive'>,
    MutationAdminSetDiscountCodeActiveArgs
  >(ADMIN_SET_DISCOUNT_CODE_ACTIVE);

  const codes = data?.adminDiscountCodes ?? [];
  const isReady = Boolean(codeText.trim() && percent.trim());

  const onCreate = async () => {
    const normalizedCode = codeText.trim().toUpperCase();
    const discountPercentage = Number(percent);
    if (!CODE_TEXT_REGEX.test(normalizedCode)) {
      setFormError('Use 3–32 letters, numbers, dashes or underscores.');
      return;
    }
    if (
      !Number.isInteger(discountPercentage) ||
      discountPercentage < 1 ||
      discountPercentage > MAX_DISCOUNT_PERCENTAGE
    ) {
      setFormError(
        `Discount is a whole number from 1 to ${MAX_DISCOUNT_PERCENTAGE}.`,
      );
      return;
    }
    if (codes.some((code) => code.codeText === normalizedCode)) {
      setFormError(`${normalizedCode} already exists.`);
      return;
    }
    // End of the chosen day in the admin's time zone
    const expiresAt = expiry
      ? new Date(
          expiry.getFullYear(),
          expiry.getMonth(),
          expiry.getDate(),
          23,
          59,
          59,
        )
      : null;
    if (expiresAt && expiresAt.getTime() <= Date.now()) {
      setFormError('Pick an expiry date in the future, or leave it empty.');
      return;
    }

    setFormError(null);
    try {
      await createCode({
        variables: {
          input: { codeText: normalizedCode, discountPercentage, expiresAt },
        },
      });
      setCodeText('');
      setPercent('');
      setExpiry(undefined);
      setSnackbarState({
        open: true,
        message: `${normalizedCode} created`,
        severity: 'success',
      });
      await refetch();
    } catch (createError) {
      SentryService.captureError(
        'Admin failed to create discount code',
        createError,
      );
      setFormError(serverMessage(createError) ?? 'Couldn’t create that code.');
    }
  };

  const onSetActive = async (id: number, isActive: boolean) => {
    try {
      await setActive({ variables: { id, isActive } });
      await refetch();
    } catch (updateError) {
      SentryService.captureError(
        'Admin failed to update discount code',
        updateError,
      );
      setSnackbarState({
        open: true,
        message: serverMessage(updateError) ?? 'Couldn’t update that code',
        severity: 'error',
      });
    }
  };

  return (
    <View style={{ gap: 24 }}>
      <Panel>
        <SectionTitle>New promo code</SectionTitle>
        <View
          style={{
            flexDirection: isDesktop ? 'row' : 'column',
            gap: 12,
            alignItems: isDesktop ? 'center' : 'stretch',
          }}
        >
          <View style={{ flex: 2 }}>
            <StaticInput
              labelText="Code"
              showLabel
              placeholderText="e.g. SPRING20"
              value={codeText}
              autoCapitalize="characters"
              onChange={(event) => setCodeText(event.nativeEvent.text)}
            />
          </View>
          <View style={{ flex: 1 }}>
            <StaticInput
              labelText="% off"
              showLabel
              placeholderText="e.g. 20"
              value={percent}
              keyboardType="number-pad"
              onChange={(event) => setPercent(event.nativeEvent.text)}
            />
          </View>
          <View style={{ flex: 2 }}>
            <DateInput
              labelText="Expires (optional)"
              showLabel
              value={expiry}
              onChange={setExpiry}
            />
          </View>
          <Button
            text="Create code"
            variant="primary"
            size="small"
            loading={creating}
            disabled={creating || !isReady}
            onPress={onCreate}
            customStyle={{ minWidth: 170 }}
          />
        </View>
        <Muted style={{ marginTop: 10, color: formError ? red : undefined }}>
          {formError ??
            `${isReady ? '' : 'Add a code and a discount to create it. '}Works with every stylist, for scheduled and instant sessions. Up to ${MAX_DISCOUNT_PERCENTAGE}% off, since Stripe needs a minimum charge.`}
        </Muted>
      </Panel>

      {loading && !data ? (
        <ActivityIndicator
          size="large"
          color={green}
          style={{ marginTop: 40 }}
        />
      ) : error && !data ? (
        <ErrorState what="promo codes" onRetry={() => refetch()} />
      ) : codes.length === 0 ? (
        <EmptyState
          title="No promo codes"
          body="Codes you create will show up here."
        />
      ) : (
        <Panel>
          {codes.map((code) => {
            const isExpired = code.expiresAt
              ? toDate(code.expiresAt).getTime() < Date.now()
              : false;
            return (
              <RowCard
                key={code.id}
                isDesktop={isDesktop}
                style={isExpired ? { opacity: 0.65 } : undefined}
              >
                <Stack style={{ flex: 2 }}>
                  <LoraText
                    style={{ fontSize: 20, color: green, letterSpacing: 0.6 }}
                  >
                    {code.codeText}
                  </LoraText>
                  <Small>
                    {code.stylistName
                      ? `Only ${code.stylistName}`
                      : 'All stylists'}
                  </Small>
                </Stack>
                <Stack style={{ flex: 2 }}>
                  <Strong>{code.discountPercentage}% off</Strong>
                  <Small>
                    {code.timesUsed
                      ? `${plural(code.timesPaid, 'paid booking')} · ${code.timesUsed} used in all`
                      : 'Not used yet'}
                  </Small>
                </Stack>
                <Stack style={{ flex: 2 }}>
                  {isExpired ? (
                    <Pill text="Expired" tone="neutral" />
                  ) : (
                    <Pill
                      text={code.isActive ? 'Active' : 'Off'}
                      tone={code.isActive ? 'good' : 'neutral'}
                    />
                  )}
                  <Small>
                    {code.expiresAt
                      ? `${isExpired ? 'Expired' : 'Expires'} ${shortDate(code.expiresAt)}`
                      : 'No expiry'}{' '}
                    · created {shortDate(code.createdAt)}
                  </Small>
                </Stack>
                <View
                  style={{
                    flex: 1.4,
                    alignItems: isDesktop ? 'flex-end' : 'flex-start',
                  }}
                >
                  {isExpired && <Small>Can’t be used</Small>}
                  {!isExpired && code.isActive && (
                    <ConfirmAction
                      label="Turn off"
                      question={`Clients won’t be able to use ${code.codeText}.`}
                      confirmText="Turn it off"
                      onConfirm={() => onSetActive(code.id, false)}
                    />
                  )}
                  {!isExpired && !code.isActive && (
                    <ActionLink
                      text="Turn on"
                      onPress={() => onSetActive(code.id, true)}
                    />
                  )}
                </View>
              </RowCard>
            );
          })}
        </Panel>
      )}
    </View>
  );
};

export default PromoCodesPanel;
